import React, { useEffect, useMemo, useState } from "react";
import {
  statementTypes, statementPrompts, buildStatementText, reviewStatementGaps,
  downloadTextFile, uid, getPromptCoaching, wordCount, saveBytesAsPdf,
  scrubRatingMentions,
} from "@/lib/claimUtils";
import {
  runStatementAudit, detectVaguePhrases, detectToneRisks,
  isMentalHealthCondition, mentalHealthPersonalPrompts,
  PREVIEW_SECTIONS, PREVIEW_SECTION_ORDER, DEFAULT_PERSONAL_SECTION_MAP,
  LENGTH_TARGETS,
} from "@/lib/statementAudit";
import { getConditionPackage } from "@/lib/conditionPrompts";
import { parseAiSections, hasAnyOverride, clearOverrides, getOverride } from "@/lib/aiDraftMerge";
import ApplyDiffModal from "@/components/ApplyDiffModal";
import {
  StatementStrengthPanel, ReviewerLensPanel, VagueFlagInline, ToneRiskInline,
  LengthToggle, GuardrailNote, EvidenceLinkButton, SectionedPreview,
} from "@/components/StatementBuilderPanels";
import {
  StatementGoalCard, ConditionPromptCard, GapsPanelV2, DraftActionButtons,
  PreviewEmptyStateV2, useScrollToPrompt,
} from "@/components/StatementWorkspace";
import { showToast } from "@/lib/toast";
import { createStatementPdf } from "@/lib/statementPdf";
import { listAppointments, listPrescriptions, listSymptoms, statementDraftAction } from "@/lib/api";
import {
  PencilLine, FileText, Eye, Warning, DownloadSimple, Plus, Trash, CaretRight, CaretDown,
  CheckCircle, Lightbulb, Copy, FilePdf, ArrowLeft, Shield, ShieldCheck, Brain, Sparkle,
} from "@phosphor-icons/react";

// ---------------------------------------------------------------------------
// Prompt resolution
//
// Personal statements pull from the condition-aware package (KNEE, BACK,
// shoulder, MH, etc). Lay-witness and nexus keep their legacy prompt sets
// but get a `section` key stamped in for the sectioned preview.
// ---------------------------------------------------------------------------

function resolvePrompts(type, conditionName) {
  if (type === "personal") {
    if (isMentalHealthCondition(conditionName)) return mentalHealthPersonalPrompts;
    const pkg = getConditionPackage(conditionName);
    if (pkg.prompts && pkg.prompts.length > 0) return pkg.prompts;
  }
  // Lay-witness / nexus / fallback — stamp section keys for sectioned preview.
  const base = statementPrompts[type] || [];
  return base.map((p) => ({
    ...p,
    section: p.section || DEFAULT_PERSONAL_SECTION_MAP[p.id] || "condition",
  }));
}

// ---------------------------------------------------------------------------
// Build a sectioned preview model for SectionedPreview
// ---------------------------------------------------------------------------

function buildSectioned(prompts, answers, opener, closing, signatureBlock = "") {
  // Group answers by section. PREVIEW_SECTION_ORDER drives the iteration order.
  const grouped = {};
  prompts.forEach((p) => {
    const v = (answers[p.id] || "").trim();
    if (!v) return;
    const section = p.section || "condition";
    if (!grouped[section]) grouped[section] = [];
    grouped[section].push(v);
  });

  // Count any AI overrides present — they substitute for the grouped answers
  // section-by-section. Overrides count as "user content" for empty-state
  // gating below.
  const overrideSections = PREVIEW_SECTION_ORDER.filter((sid) => getOverride(answers, sid));

  // If the veteran hasn't typed anything yet AND no overrides are applied,
  // return ZERO sections so the SectionedPreview can render the empty-state
  // coaching copy. Pushing the opener/closing boilerplate before any
  // content would hide the empty state — the testing-agent caught this in
  // iteration_31.
  const hasUserContent = Object.keys(grouped).length > 0 || overrideSections.length > 0;
  if (!hasUserContent) return [];

  const sections = [];
  if (opener) sections.push({ id: "opener", title: "Statement of Condition", body: opener });

  PREVIEW_SECTION_ORDER.forEach((sid) => {
    if (sid === "condition" || sid === "closing") return;
    // Override wins over the structured group — that's the whole point of
    // "Apply to Draft." Vet can revert with the toolbar Revert button.
    const override = getOverride(answers, sid);
    if (override) {
      sections.push({ id: sid, title: PREVIEW_SECTIONS[sid], body: override, aiApplied: true });
      return;
    }
    const items = grouped[sid] || [];
    if (items.length === 0) return;
    sections.push({ id: sid, title: PREVIEW_SECTIONS[sid], body: items.join("\n\n") });
  });

  if (closing) sections.push({ id: "closing", title: "Closing Statement", body: closing });
  if (signatureBlock) sections.push({ id: "signature", title: "Signature", body: signatureBlock });
  return sections;
}

// ---------------------------------------------------------------------------
// StatementsView (default export)
// ---------------------------------------------------------------------------

export default function StatementsView({ issues, evidence = [], statements, setStatements }) {
  const [activeId, setActiveId] = useState(null);
  const [openPromptId, setOpenPromptId] = useState(null);
  const [copied, setCopied] = useState(false);
  const [pdfStatus, setPdfStatus] = useState("");
  const [printMode, setPrintMode] = useState(false);
  const [lengthTarget, setLengthTarget] = useState("standard");

  // Draft-action (LLM) state
  const [draftRunning, setDraftRunning] = useState(null);   // action id or null
  const [draftOutput, setDraftOutput] = useState(null);     // { action, output } | null
  const [draftError, setDraftError] = useState("");

  // Apply-to-draft modal state — holds the parsed AI sections waiting for the
  // vet to opt in to each section.
  const [applyDiff, setApplyDiff] = useState(null);  // { parsed, action } | null

  const scrollAndOpen = useScrollToPrompt();

  // Backend-loaded evidence sources (loaded lazily once when first editor opens)
  const [appointments, setAppointments] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [sourcesLoaded, setSourcesLoaded] = useState(false);

  useEffect(() => {
    if (!activeId || sourcesLoaded) return;
    let cancelled = false;
    Promise.allSettled([listAppointments(), listPrescriptions(), listSymptoms()]).then((results) => {
      if (cancelled) return;
      setAppointments(results[0].status === "fulfilled" ? results[0].value : []);
      setPrescriptions(results[1].status === "fulfilled" ? results[1].value : []);
      setSymptoms(results[2].status === "fulfilled" ? results[2].value : []);
      setSourcesLoaded(true);
    });
    return () => { cancelled = true; };
  }, [activeId, sourcesLoaded]);

  // Builds the export text for a statement, honoring print-mode scrub.
  function exportTextFor(stmt) {
    const raw = buildStatementText(stmt.type, stmt.answers);
    if (!raw) return { text: "", scrub: { removedCount: 0, removedSamples: [] } };
    if (!printMode) return { text: raw, scrub: { removedCount: 0, removedSamples: [] } };
    const result = scrubRatingMentions(raw);
    return { text: result.text, scrub: { removedCount: result.removedCount, removedSamples: result.removedSamples } };
  }

  function addStatement(type) {
    const newStatement = {
      id: `stmt-${uid()}`,
      type,
      conditionId: issues[0]?.id || "",
      conditionName: issues[0]?.claimedAs || "",
      answers: {},
      evidenceLinks: {},
      createdAt: new Date().toISOString(),
    };
    setStatements((prev) => [newStatement, ...prev]);
    setActiveId(newStatement.id);
    const prompts = resolvePrompts(type, newStatement.conditionName);
    setOpenPromptId(prompts[0]?.id || null);
  }

  function updateStatement(id, patch) {
    setStatements((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }

  function updateAnswer(stmt, promptId, value) {
    updateStatement(stmt.id, { answers: { ...stmt.answers, [promptId]: value } });
  }

  function updateEvidenceLinks(stmt, promptId, links) {
    const next = { ...(stmt.evidenceLinks || {}), [promptId]: links };
    updateStatement(stmt.id, { evidenceLinks: next });
  }

  function deleteStatement(id) {
    if (!window.confirm("Delete this statement?")) return;
    const removed = statements.find((s) => s.id === id);
    setStatements((prev) => prev.filter((s) => s.id !== id));
    if (activeId === id) setActiveId(null);
    if (removed) {
      showToast({
        message: `Statement "${removed.conditionName || "General"}" deleted.`,
        duration: 6000,
        action: {
          label: "UNDO",
          onClick: () => {
            setStatements((prev) => [removed, ...prev]);
            showToast({ variant: "success", message: "Statement restored.", duration: 3000 });
          },
        },
      });
    }
  }

  function exportStatement(stmt) {
    const { text } = exportTextFor(stmt);
    if (!text) return;
    const typeInfo = statementTypes.find((t) => t.id === stmt.type);
    const suffix = printMode ? "-print" : "";
    downloadTextFile(`BeTheNexus-${typeInfo?.title || "Statement"}-${stmt.conditionName || "general"}${suffix}.txt`, text);
  }

  async function exportStatementPdf(stmt) {
    setPdfStatus("");
    try {
      const typeInfo = statementTypes.find((t) => t.id === stmt.type);
      const { text } = exportTextFor(stmt);
      if (!text) { setPdfStatus("Nothing to export — fill in some prompts first."); return; }
      const exportStmt = printMode
        ? { ...stmt, answers: { ...stmt.answers, __printScrubbed: text }, __printScrubText: text }
        : stmt;
      const bytes = await createStatementPdf({
        statement: exportStmt,
        veteranName: stmt.conditionName || "",
        statementType: typeInfo,
        overrideText: printMode ? text : null,
      });
      const safeName = (stmt.conditionName || "general").replace(/[^a-zA-Z0-9-_]+/g, "-");
      const suffix = printMode ? "-print" : "";
      saveBytesAsPdf(bytes, `BeTheNexus-${typeInfo?.title?.replace(/[^a-zA-Z0-9-_]+/g, "-") || "Statement"}-${safeName}${suffix}.pdf`);
      setPdfStatus(printMode ? "Print-mode PDF downloaded (rating mentions stripped)." : "Statement PDF downloaded.");
      setTimeout(() => setPdfStatus(""), 2500);
    } catch (err) {
      console.error(err);
      setPdfStatus(`Error: ${err.message}`);
    }
  }

  async function copyText(stmt) {
    const { text } = exportTextFor(stmt);
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) { console.error(err); }
  }

  const grouped = useMemo(() => statementTypes.map((type) => ({
    ...type,
    items: statements
      .filter((s) => s.type === type.id)
      .slice()
      .sort((a, b) => (a.title || "").localeCompare(b.title || "")),
  })), [statements]);

  const sortedIssues = useMemo(
    () => [...issues].sort((a, b) => (a.claimedAs || "").localeCompare(b.claimedAs || "")),
    [issues],
  );

  const activeStatement = activeId ? statements.find((s) => s.id === activeId) : null;
  const activePrompts = useMemo(
    () => (activeStatement ? resolvePrompts(activeStatement.type, activeStatement.conditionName) : []),
    [activeStatement],
  );
  const activeType = activeStatement ? statementTypes.find((t) => t.id === activeStatement.type) : null;
  const activeGaps = useMemo(
    () => (activeStatement ? reviewStatementGaps(activeStatement.type, activeStatement.answers) : []),
    [activeStatement],
  );
  const activeText = useMemo(
    () => (activeStatement ? buildStatementText(activeStatement.type, activeStatement.answers) : ""),
    [activeStatement],
  );
  const activeScrub = useMemo(
    () => (activeStatement && printMode ? scrubRatingMentions(activeText) : { text: activeText, removedCount: 0, removedSamples: [] }),
    [activeStatement, activeText, printMode],
  );
  const previewText = printMode ? activeScrub.text : activeText;
  const totalWords = wordCount(previewText);

  // Audit
  const audit = useMemo(
    () => (activeStatement
      ? runStatementAudit({
          type: activeStatement.type,
          answers: activeStatement.answers,
          conditionName: activeStatement.conditionName,
          evidenceLinks: activeStatement.evidenceLinks || {},
        })
      : null),
    [activeStatement],
  );

  // Condition-aware Statement Goal + category badge (Personal statements only)
  const conditionPackage = useMemo(
    () => (activeStatement && activeStatement.type === "personal"
      ? getConditionPackage(activeStatement.conditionName)
      : null),
    [activeStatement],
  );

  // Fix-now: open the target prompt + scroll its card into view
  function handleJumpTo(promptId) {
    scrollAndOpen(promptId, setOpenPromptId);
  }

  // Draft-action runner — calls /api/statements/draft-action with the right payload.
  // For "generate" we serialize the prompt/answer pairs as raw input; for the other
  // actions we send the current rendered preview text.
  async function runDraftAction(actionId) {
    if (!activeStatement) return;
    setDraftError("");
    setDraftRunning(actionId);
    try {
      let currentText = "";
      if (actionId === "generate") {
        // Hand the LLM the structured Q/A blob so it can build a coherent draft.
        currentText = activePrompts.map((p) => {
          const ans = (activeStatement.answers[p.id] || "").trim();
          return ans ? `[${p.label}]\n${ans}` : "";
        }).filter(Boolean).join("\n\n");
        if (!currentText) {
          setDraftError("Answer at least one prompt first.");
          setDraftRunning(null);
          return;
        }
      } else {
        currentText = (previewText || "").trim();
        if (!currentText) {
          setDraftError("Nothing to work with — write some answers first.");
          setDraftRunning(null);
          return;
        }
      }
      const ctx = `${activeType?.title || "Personal Statement"} for "${activeStatement.conditionName || "general condition"}"`;
      const result = await statementDraftAction({
        action: actionId,
        statementContext: ctx,
        currentText,
      });
      setDraftOutput(result);
    } catch (err) {
      // Surface the backend's error message when available.
      const msg = err?.response?.data?.detail || err.message || "Draft service failed.";
      setDraftError(typeof msg === "string" ? msg : "Draft service failed.");
    } finally {
      setDraftRunning(null);
    }
  }

  // Open the diff modal with the parsed AI sections. If parsing finds no
  // recognised section headers (rare — the LLM strayed from the contract),
  // we STILL open the modal so it can render the explanatory empty-state
  // copy in-context, instead of a fleeting toast the vet would miss.
  function handleApplyDraft(rawOutput, action) {
    if (!activeStatement || !rawOutput) return;
    const parsed = parseAiSections(rawOutput);
    setApplyDiff({ parsed: parsed || {}, action });
  }

  function handleApplyConfirm(nextAnswers, appliedSectionIds) {
    if (!activeStatement) return;
    updateStatement(activeStatement.id, { answers: nextAnswers });
    setApplyDiff(null);
    setDraftOutput(null);
    showToast({
      variant: "success",
      message: `Applied ${appliedSectionIds.length} section${appliedSectionIds.length === 1 ? "" : "s"} to your draft.`,
      action: {
        label: "REVERT",
        onClick: () => {
          updateStatement(activeStatement.id, { answers: clearOverrides(nextAnswers) });
          showToast({ variant: "info", message: "AI changes reverted.", duration: 2500 });
        },
      },
      duration: 6000,
    });
  }

  function handleRevertOverrides() {
    if (!activeStatement) return;
    updateStatement(activeStatement.id, { answers: clearOverrides(activeStatement.answers || {}) });
    showToast({ variant: "info", message: "AI changes reverted — original answers restored in the preview.", duration: 3500 });
  }

  // Sectioned preview model
  const previewSections = useMemo(() => {
    if (!activeStatement) return [];
    const opener = activeStatement.type === "lay-witness"
      ? "I am submitting this statement to describe what I have personally observed."
      : activeStatement.type === "nexus"
        ? "This letter sets out my medical opinion regarding the veteran identified below."
        : "I am submitting this statement in my own words, to the best of my recollection.";
    const closing = activeStatement.type === "nexus"
      ? "The opinions stated in this letter are based on my professional judgment and review of the records identified above."
      : "The statements above are true and correct to the best of my knowledge.";
    const sig = activeStatement.type === "nexus" ? (activeStatement.answers["signature-block"] || "").trim() : "";
    let answersForSections = activeStatement.answers;
    if (printMode) {
      // For the sectioned preview in print mode, scrub each answer individually
      // so section grouping survives the rating-language scrub.
      const scrubbed = {};
      Object.entries(activeStatement.answers).forEach(([k, v]) => {
        scrubbed[k] = scrubRatingMentions(v || "").text;
      });
      answersForSections = scrubbed;
    }
    return buildSectioned(activePrompts, answersForSections, opener, closing, sig);
  }, [activeStatement, activePrompts, printMode]);

  // ---- Hub view (no statement selected) ----
  if (!activeStatement) {
    return (
      <div className="view-grid" data-testid="statements-view">
        <div className="panel" data-testid="statements-overview-panel">
          <div className="panel-header">
            <PencilLine size={18} weight="bold" />
            <span>STATEMENT BUILDER</span>
            <span className="header-count mono">{statements.length}</span>
          </div>
          <div className="panel-body">
            <p className="panel-desc">
              Build three types of statements with guided prompts, a live <strong>Statement Strength</strong> audit,
              evidence linking, and a branded PDF you can send to your provider or VSO.
            </p>
          </div>
        </div>

        <div className="stmt-type-cards" data-testid="stmt-type-cards">
          {grouped.map((g) => (
            <div key={g.id} className="stmt-type-card" data-testid={`stmt-type-${g.id}`}>
              <div className="stmt-type-card-head">
                <FileText size={18} weight="bold" />
                <h3 className="stmt-type-card-title">{g.title}</h3>
                {g.items.length > 0 && <span className="stmt-type-card-count mono">{g.items.length} draft{g.items.length === 1 ? "" : "s"}</span>}
              </div>
              <p className="stmt-type-card-desc">{g.description}</p>
              <div className="stmt-type-card-meta">
                <div><span className="stmt-type-card-meta-label">FORM</span><span>{g.formNumber}</span></div>
                <div><span className="stmt-type-card-meta-label">WHO WRITES</span><span>{g.whoWrites}</span></div>
                <div><span className="stmt-type-card-meta-label">WHEN TO USE</span><span>{g.whenToUse}</span></div>
              </div>
              <button className="btn btn--accent stmt-type-card-cta" onClick={() => addStatement(g.id)} data-testid={`add-stmt-${g.id}`}>
                <Plus size={14} weight="bold" /> START NEW {g.title.toUpperCase().split("(")[0].trim()}
              </button>
              {g.items.length > 0 && (
                <div className="stmt-type-card-drafts">
                  <div className="stmt-type-card-drafts-label">YOUR DRAFTS</div>
                  {g.items.map((stmt) => {
                    const a = runStatementAudit({
                      type: stmt.type,
                      answers: stmt.answers,
                      conditionName: stmt.conditionName,
                      evidenceLinks: stmt.evidenceLinks || {},
                    });
                    const strongCount = a.dimensions.filter((d) => d.status === "strong").length;
                    const pp = Math.round((strongCount / a.dimensions.length) * 100);
                    return (
                      <div key={stmt.id} className="stmt-draft-row" data-testid={`stmt-item-${stmt.id}`}>
                        <button className="stmt-draft-main" onClick={() => { setActiveId(stmt.id); setOpenPromptId(null); }} data-testid={`open-stmt-${stmt.id}`}>
                          <div className="stmt-draft-name">{stmt.conditionName || "General"}</div>
                          <div className="stmt-draft-progress">
                            <div className="stmt-draft-bar"><span style={{ width: `${pp}%` }} /></div>
                            <span className="stmt-draft-pct mono">{strongCount}/{a.dimensions.length} STRONG</span>
                          </div>
                        </button>
                        <button className="btn btn--danger btn--sm" onClick={() => deleteStatement(stmt.id)} data-testid={`delete-stmt-${stmt.id}`}>
                          <Trash size={12} weight="bold" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ---- Build evidence-link source list ----
  const linkSources = [
    ...evidence.map((e) => ({
      kind: "evidence",
      id: e.id,
      label: `${e.type || "Record"} — ${e.title || e.description || "Untitled"}${e.linkedIssueName ? ` (${e.linkedIssueName})` : ""}`,
    })),
    ...statements.filter((s) => s.id !== activeStatement.id && s.type === "lay-witness").map((s) => ({
      kind: "lay-witness",
      id: s.id,
      label: s.conditionName || "Lay/Witness Statement",
    })),
    ...appointments.map((a) => ({
      kind: "appointment",
      id: a.id,
      label: `${a.title || a.provider || "Appointment"}${a.scheduledFor ? ` — ${new Date(a.scheduledFor).toLocaleDateString()}` : ""}`,
    })),
    ...symptoms.map((s) => ({
      kind: "symptom",
      id: s.id,
      label: `${s.symptomName || s.description || "Symptom log"}${s.date ? ` — ${new Date(s.date).toLocaleDateString()}` : ""}`,
    })),
    ...prescriptions.map((p) => ({
      kind: "prescription",
      id: p.id,
      label: `${p.medName || "Rx"}${p.dosage ? ` ${p.dosage}` : ""}`,
    })),
  ];

  const isMentalHealth = isMentalHealthCondition(activeStatement.conditionName);

  // ---- Editor view ----
  return (
    <div className="view-grid stmt-editor-grid" data-testid="statements-view-editor">
      <div className="panel stmt-editor-toolbar" data-testid="stmt-editor-toolbar">
        <div className="stmt-toolbar-row">
          <button className="btn btn--ghost btn--sm" onClick={() => setActiveId(null)} data-testid="stmt-back-btn">
            <ArrowLeft size={14} weight="bold" /> BACK TO LIST
          </button>
          <div className="stmt-toolbar-title">
            <FileText size={16} weight="bold" />
            <span>{activeType?.title}</span>
            <span className="stmt-toolbar-cond">{activeStatement.conditionName || "General"}</span>
            {isMentalHealth && (
              <span
                data-testid="stmt-mh-badge"
                title="Mental-health condition detected — prompt set adapted to occupational + social impairment, sleep, mood, panic, treatment."
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "2px 8px",
                  background: "rgba(31,58,107,0.10)",
                  border: "1px solid rgba(31,58,107,0.30)",
                  borderRadius: 999,
                  fontSize: 10,
                  fontWeight: 800,
                  color: "#1F3A6B",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                <Brain size={11} weight="fill" /> Mental Health prompts
              </span>
            )}
          </div>
          <div className="stmt-toolbar-actions">
            {hasAnyOverride(activeStatement.answers || {}) && (
              <button
                className="btn btn--outline btn--sm"
                onClick={handleRevertOverrides}
                data-testid="stmt-revert-ai-btn"
                title="Restore the structured-answer preview and clear AI-applied sections."
                style={{ borderColor: "rgba(31,58,107,0.40)", color: "#1F3A6B" }}
              >
                <Sparkle size={12} weight="bold" /> REVERT AI CHANGES
              </button>
            )}
            <button
              className={`btn btn--sm ${printMode ? "btn--accent" : "btn--outline"}`}
              onClick={() => setPrintMode((p) => !p)}
              data-testid="stmt-print-mode-toggle"
              title={printMode
                ? "Print mode ON — sentences mentioning rating tiers are stripped from copy / TXT / PDF."
                : "Turn on Print Mode to strip rating percentages from exports."}
            >
              {printMode ? <ShieldCheck size={12} weight="bold" /> : <Shield size={12} weight="bold" />}
              {" "}PRINT MODE{printMode ? ": ON" : ""}
            </button>
            <button className="btn btn--outline btn--sm" onClick={() => copyText(activeStatement)} data-testid="stmt-copy-btn">
              <Copy size={12} weight="bold" /> {copied ? "COPIED" : "COPY"}
            </button>
            <button className="btn btn--outline btn--sm" onClick={() => exportStatement(activeStatement)} data-testid="export-stmt-btn">
              <DownloadSimple size={12} weight="bold" /> TXT
            </button>
            <button className="btn btn--accent btn--sm" onClick={() => exportStatementPdf(activeStatement)} data-testid="export-stmt-pdf-btn">
              <FilePdf size={12} weight="bold" /> BRANDED PDF
            </button>
          </div>
        </div>
        <div className="stmt-progress-row" style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
          <LengthToggle target={lengthTarget} onChange={setLengthTarget} currentWordCount={totalWords} />
        </div>
        {pdfStatus && <div className="form-report" data-testid="stmt-pdf-status">{pdfStatus}</div>}
      </div>

      {/* Audit panels — span full width above the editor cols */}
      {audit && (
        <div style={{ display: "grid", gap: 12 }}>
          <StatementStrengthPanel dimensions={audit.dimensions} />
          <ReviewerLensPanel lens={audit.reviewerLens} />
        </div>
      )}

      <div className="stmt-editor-cols">
        {/* LEFT: prompts accordion */}
        <div className="stmt-prompts-col">
          <div style={{ display: "grid", gap: 14 }}>
            {/* Condition-aware Statement Goal — only for Personal statements */}
            {conditionPackage && (
              <StatementGoalCard
                goal={conditionPackage.goal}
                category={conditionPackage.category}
                side={conditionPackage.side}
              />
            )}

            <div className="panel">
              <div className="panel-body" style={{ padding: 16 }}>
                <label className="field" style={{ marginBottom: 12 }}>
                  <span className="field-label">LINKED CONDITION</span>
                  <select
                    className="field-input"
                    value={activeStatement.conditionId}
                    onChange={(e) => {
                      const issue = issues.find((i) => i.id === e.target.value);
                      updateStatement(activeStatement.id, { conditionId: e.target.value, conditionName: issue?.claimedAs || "General" });
                    }}
                    data-testid="stmt-condition-select"
                  >
                    <option value="">General (not linked)</option>
                    {sortedIssues.map((i) => <option key={i.id} value={i.id}>{i.claimedAs}</option>)}
                  </select>
                </label>

                <div className="stmt-prompt-list">
                  {activePrompts.map((prompt) => (
                    <ConditionPromptCard
                      key={prompt.id}
                      prompt={prompt}
                      type={activeStatement.type}
                      value={activeStatement.answers[prompt.id]}
                      onChange={(val) => updateAnswer(activeStatement, prompt.id, val)}
                      isOpen={openPromptId === prompt.id}
                      onToggle={() => setOpenPromptId(openPromptId === prompt.id ? null : prompt.id)}
                      evidenceLinks={(activeStatement.evidenceLinks || {})[prompt.id] || []}
                      onEvidenceChange={(links) => updateEvidenceLinks(activeStatement, prompt.id, links)}
                      sources={linkSources}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Actionable gaps (Required + Recommended buckets, with Fix-now buttons) */}
            {audit && (
              <GapsPanelV2
                dimensions={audit.dimensions}
                prompts={activePrompts}
                onJumpTo={handleJumpTo}
              />
            )}

            {/* Draft Action Buttons — LLM-powered rewrites + critique */}
            <DraftActionButtons
              hasContent={(previewText || "").trim().length > 0
                || Object.values(activeStatement.answers || {}).some((v) => (v || "").trim().length > 0)}
              runningAction={draftRunning}
              lastOutput={draftOutput}
              error={draftError}
              onRun={runDraftAction}
              onClear={() => { setDraftOutput(null); setDraftError(""); }}
              onApply={handleApplyDraft}
            />
          </div>
        </div>

        {/* RIGHT: sticky live preview with sections */}
        <div className="stmt-preview-col">
          <div className="panel stmt-preview-panel" data-testid="stmt-preview-panel">
            <div className="panel-header">
              <Eye size={16} weight="bold" />
              <span>LIVE PREVIEW{printMode ? " — PRINT MODE" : ""}</span>
              {previewText && <span className="header-count mono">{totalWords}w</span>}
            </div>
            <div className="panel-body">
              <GuardrailNote />

              {printMode && activeScrub.removedCount > 0 && (
                <div
                  data-testid="stmt-scrub-banner"
                  style={{
                    background: "rgba(16, 185, 129, 0.10)", border: "1px solid rgba(16, 185, 129, 0.30)", borderRadius: 6,
                    padding: "8px 10px", marginBottom: 10, fontSize: 12, color: "var(--text-primary)",
                  }}
                >
                  <strong>Print mode stripped {activeScrub.removedCount} sentence{activeScrub.removedCount === 1 ? "" : "s"}</strong> that mentioned a rating tier or percentage.
                  {activeScrub.removedSamples.length > 0 && (
                    <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontStyle: "italic" }}>
                      {activeScrub.removedSamples.map((s) => (
                        <li key={s} data-testid={`stmt-scrub-sample-${s.slice(0, 24)}`}>{s}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {printMode && activeText && activeScrub.removedCount === 0 && (
                <div
                  data-testid="stmt-scrub-clean"
                  style={{
                    background: "var(--branch-accent-soft, rgba(31, 58, 107, 0.08))", border: "1px solid var(--branch-accent, #1F3A6B)", borderRadius: 6,
                    padding: "8px 10px", marginBottom: 10, fontSize: 12, color: "var(--text-primary)",
                  }}
                >
                  Print mode is <strong>ON</strong> and your statement contains no rating-tier language. Safe to share.
                </div>
              )}
              <SectionedPreview sections={previewSections} />
              {/* Plain-text fallback also rendered (hidden) for legacy tests + copy fallback */}
              <div data-testid="stmt-preview" style={{ display: "none" }}>{previewText}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Apply-to-Draft section-by-section diff modal */}
      {applyDiff && (
        <ApplyDiffModal
          prompts={activePrompts}
          answers={activeStatement.answers || {}}
          parsedSections={applyDiff.parsed}
          onCancel={() => setApplyDiff(null)}
          onApply={handleApplyConfirm}
        />
      )}
    </div>
  );
}
